import type { FC } from "react";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { Button } from "@/components/base/buttons/button";
import { blockedActionProps } from "@/components/app/blockedAction";
import { cx } from "@/utils/cx";

/**
 * Ikonová akce v řádku tabulky — UUI `Tooltip` kolem `Button`.
 *
 * Samotné `aria-label` vidí jen odečítač obrazovky; u řady ikon vedle sebe
 * není myší poznat, co která dělá. Nativní `title` tady nepomůže: `Button` je
 * react-aria komponenta, `title` nemá v typovém rozhraní a prochází přes
 * `filterDOMProps` — `Tooltip` je cesta, kterou design systém používá všude
 * jinde (viz `base/input/label.tsx`).
 *
 * `label` je jeden zdroj pravdy pro tooltip i `aria-label`. S vyplněným `href`
 * vyrenderuje `Button` react-aria odkaz, který přes `AriaRouterProvider`
 * (`main.tsx`) naviguje v SPA a zároveň umí „otevřít v novém panelu“.
 */
interface IconActionProps {
  icon: FC<{ className?: string }>;
  /** Popisek do tooltipu i do `aria-label`. */
  label: string;
  onPress?: () => void;
  /** Cílová cesta — z akce se stane odkaz místo tlačítka. */
  href?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  /**
   * Hotový (přeložený) důvod, proč akce nejde spustit. Vypíše se jako druhý
   * řádek tooltipu a akci zakáže — bez nativního `disabled`, aby tooltip
   * vůbec vystřelil (viz `blockedActionProps`).
   */
  disabledReason?: string;
  color?: "tertiary" | "tertiary-destructive";
  className?: string;
}

export function IconAction({ icon, label, onPress, href, isLoading, isDisabled, disabledReason, color = "tertiary", className }: IconActionProps) {
  const blocked = blockedActionProps(disabledReason);
  const common = { size: "sm", color, iconLeading: icon, "aria-label": label, ...blocked, className: cx(className, blocked.className) } as const;

  return (
    <Tooltip title={label} description={disabledReason} placement="top">
      {/* Blokovaná akce nesmí zůstat odkazem — jinak by ji šlo projít klikem. */}
      {href && !disabledReason ? (
        <Button {...common} href={href} isDisabled={isDisabled} />
      ) : (
        <Button {...common} onPress={disabledReason ? undefined : onPress} isLoading={isLoading} isDisabled={isDisabled} />
      )}
    </Tooltip>
  );
}
